/**
 * Where a receipt goes: a thermal printer, or the browser's print dialog.
 *
 * ## The bug this module exists to fix (SITE_TRIAGE bucket B1)
 *
 * Every print site in the app used to ask `currentDeviceType === DeviceType.DESKTOP`
 * and, if so, call `window.print()`. That worked only because `DESKTOP` and
 * "a browser tab with no printer" were the same thing.
 *
 * On an Electron register they are not. `DeviceType` stays `DESKTOP` — it drives
 * the desktop header and layout, and the plan keeps it that way (A6,
 * `PlatformDevice` row) — while the register has a **real Star printer on the
 * LAN**. Asking `DeviceType` there routes a customer's receipt into a browser
 * print dialog on a machine with a thermal printer plugged in, which is not a
 * crash, just a shop that cannot hand anyone a receipt.
 *
 * So the question is asked of the capability table instead, and the answer has
 * two parts — *can* this runtime print thermally, and is a printer actually
 * set up right now:
 *
 * | Runtime  | `thermalPrinting`   | No printer selected | Printer selected |
 * |----------|---------------------|---------------------|------------------|
 * | ios      | `star-native`       | prompt for one      | thermal          |
 * | android  | `star-native`       | prompt for one      | thermal          |
 * | web      | `mock`              | browser dialog      | browser dialog   |
 * | electron | `star-electron-lan` | browser dialog      | **thermal**      |
 *
 * The desktop fallback is deliberate and is what the plan asks for: a register
 * with no printer configured still has a print dialog and a PDF exporter, so
 * `window.print()` is a better answer than nothing — but only *after* the real
 * printer has been ruled out, never instead of looking.
 */
import { usePrinterStore } from 'src/store/PrinterStore';
import { getPlatformPrinter } from 'src/platform/services';

/** Whether this runtime has a real, non-simulated thermal printing backend. */
export function hasThermalPrinting(): boolean {
  const { thermalPrinting } = getPlatformPrinter();
  return thermalPrinting === 'star-native' || thermalPrinting === 'star-electron-lan';
}

/** Whether `window.print()` is a sensible receipt route on this runtime. */
export function hasBrowserPrintDialog(): boolean {
  return getPlatformPrinter().desktopPrintDialog;
}

export type ReceiptRoute = 'thermal' | 'browser';

/**
 * Resolve the route for one receipt.
 *
 * `hasActivePrinter` is the store's current answer, not a capability: a phone
 * with no printer chosen still routes `'thermal'`, because `printingService`
 * responds by prompting the operator to pick one — which is the correct
 * behaviour and the one the mobile app already has.
 */
export function resolveReceiptRoute(hasActivePrinter: boolean): ReceiptRoute {
  if (!hasThermalPrinting()) return 'browser';
  if (hasActivePrinter) return 'thermal';
  // A real backend but nothing configured yet. Prefer the print dialog where
  // one exists (a desktop register), and otherwise stay on the thermal path so
  // the operator is asked to select a printer rather than silently given
  // nothing.
  return hasBrowserPrintDialog() ? 'browser' : 'thermal';
}

/** Reactive `resolveReceiptRoute` for components. */
export function useReceiptRoute(): ReceiptRoute {
  const activePrinter = usePrinterStore((state) => state.activePrinter);
  return resolveReceiptRoute(activePrinter !== null);
}

/**
 * Whether this receipt should print through the browser rather than a printer.
 * The direct replacement for the old `currentDeviceType === DeviceType.DESKTOP`
 * at every print site.
 */
export function useBrowserReceiptPrint(): boolean {
  return useReceiptRoute() === 'browser';
}

/**
 * Whether the printer-health machinery — readiness polling, background
 * auto-reconnect, the "printer needs attention" affordance and the troubleshoot
 * dialog — has anything to act on.
 *
 * An alias of `hasThermalPrinting()`, named for what those call sites mean.
 * They previously asked `Capacitor.isNativePlatform()`, which is a capability
 * wearing a platform's clothes, and one that quietly became wrong the moment
 * Electron started reporting itself as native (AMENDMENT A3.2).
 */
export const printerHealthTrackingApplies = hasThermalPrinting;
